import React, { useCallback, useMemo, useRef, useState } from "react";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil, faTrash } from "@fortawesome/free-solid-svg-icons";
import api from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { getHelpRouteOptions } from "../utils/helpConfig";
import InlineFieldTooltip from "../components/InlineFieldTooltip";
import "./helpPage.css";

const categoryOptions = [
  "General",
  "Dashboard",
  "Bridges",
  "Simulation",
  "Repair Projects",
  "Masters Management",
  "Administration",
  "Settings",
  "Profile",
];

const emptyDocument = {
  title: "",
  slug: "",
  routePath: "",
  category: "General",
  contentHtml: "",
  isPublished: true,
  isAdminOnly: false,
  sortOrder: 0,
};

const Help = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [routeFilter, setRouteFilter] = useState("");
  const [activeCategory, setActiveCategory] = useState("");
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(emptyDocument);
  const [saving, setSaving] = useState(false);
  const editorRef = useRef(null);
  const quillRef = useRef(null);
  const autoEditHandledRef = useRef(false);

  const loadDocuments = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get("/help/documents");
      const docs = response.data.documents || [];
      setDocuments(docs);
      return docs;
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load help documents.");
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  React.useEffect(() => {
    if (!isAdmin || documents.length === 0 || autoEditHandledRef.current)
      return;

    const params = new URLSearchParams(window.location.search);
    const editSlug = params.get("edit");
    if (!editSlug) return;

    const docToEdit = documents.find((doc) => doc.slug === editSlug);
    if (!docToEdit) return;

    setSelectedDoc(docToEdit);
    setFormData({
      title: docToEdit.title,
      slug: docToEdit.slug,
      routePath: docToEdit.routePath || "",
      category: docToEdit.category || "General",
      contentHtml: docToEdit.contentHtml || "",
      isPublished: docToEdit.isPublished,
      isAdminOnly: docToEdit.isAdminOnly,
      sortOrder: docToEdit.sortOrder || 0,
    });
    setIsEditing(true);
    autoEditHandledRef.current = true;

    params.delete("edit");
    const nextQuery = params.toString();
    const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ""}`;
    window.history.replaceState({}, "", nextUrl);
  }, [documents, isAdmin]);

  const filteredDocuments = useMemo(() => {
    return documents.filter((doc) => {
      const matchesSearch =
        !searchTerm ||
        doc.title.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory =
        !categoryFilter || doc.category === categoryFilter;
      const matchesRoute = !routeFilter || doc.routePath === routeFilter;
      return matchesSearch && matchesCategory && matchesRoute;
    });
  }, [documents, searchTerm, categoryFilter, routeFilter]);

  const groupedDocuments = useMemo(() => {
    return filteredDocuments.reduce((acc, doc) => {
      const key = doc.category || "General";
      if (!acc[key]) acc[key] = [];
      acc[key].push(doc);
      return acc;
    }, {});
  }, [filteredDocuments]);

  const sortedCategories = useMemo(
    () => Object.keys(groupedDocuments).sort((a, b) => a.localeCompare(b)),
    [groupedDocuments],
  );

  const activeCategoryDocs = useMemo(
    () => (activeCategory ? groupedDocuments[activeCategory] || [] : []),
    [activeCategory, groupedDocuments],
  );

  React.useEffect(() => {
    if (sortedCategories.length === 0) {
      if (activeCategory) setActiveCategory("");
      return;
    }

    if (!activeCategory || !sortedCategories.includes(activeCategory)) {
      setActiveCategory(sortedCategories[0]);
    }
  }, [sortedCategories, activeCategory]);

  const startCreate = () => {
    setSelectedDoc(null);
    setFormData({ ...emptyDocument });
    setIsEditing(true);
  };

  const startEdit = (doc) => {
    setSelectedDoc(doc);
    setFormData({
      title: doc.title,
      slug: doc.slug,
      routePath: doc.routePath || "",
      category: doc.category || "General",
      contentHtml: doc.contentHtml || "",
      isPublished: doc.isPublished,
      isAdminOnly: doc.isAdminOnly,
      sortOrder: doc.sortOrder || 0,
    });
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    if (selectedDoc) {
      setFormData({
        title: selectedDoc.title,
        slug: selectedDoc.slug,
        routePath: selectedDoc.routePath || "",
        category: selectedDoc.category || "General",
        contentHtml: selectedDoc.contentHtml || "",
        isPublished: selectedDoc.isPublished,
        isAdminOnly: selectedDoc.isAdminOnly,
        sortOrder: selectedDoc.sortOrder || 0,
      });
    } else {
      setFormData({ ...emptyDocument });
    }
  };

  const handleSave = async () => {
    const routeSlug = resolveRouteSlug(formData.routePath);
    if (!routeSlug) {
      setError("Route Path is required and must map to a valid help route.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        slug: routeSlug,
      };

      if (selectedDoc?.id) {
        await api.put(`/help/document/${selectedDoc.id}`, payload);
      } else {
        await api.post("/help/documents", payload);
      }
      setIsEditing(false);
      const refreshedDocs = await loadDocuments();

      // Keep detail view in sync with server after save.
      if (selectedDoc?.id) {
        const updated = refreshedDocs.find((doc) => doc.id === selectedDoc.id);
        if (updated) {
          setSelectedDoc(updated);
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save help document.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.title}"?`)) return;
    try {
      await api.delete(`/help/document/${doc.id}`);
      if (selectedDoc?.id === doc.id) {
        setSelectedDoc(null);
        setIsEditing(false);
      }
      await loadDocuments();
    } catch (err) {
      setError(
        err.response?.data?.message || "Failed to delete help document.",
      );
    }
  };

  const handleQuillImage = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      const fd = new FormData();
      fd.append("file", file);
      try {
        const response = await api.post("/help/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const editor = quillRef.current;
        const range = editor?.getSelection(true);
        if (range && editor) {
          editor.insertEmbed(range.index, "image", response.data.url, "user");
        }
      } catch (err) {
        setError(err.response?.data?.message || "Image upload failed.");
      }
    };
    input.click();
  }, []);

  const quillToolbar = useMemo(
    () => [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link", "image"],
      ["clean"],
    ],
    [],
  );

  const routeOptions = useMemo(() => getHelpRouteOptions(), []);
  const resolveRouteSlug = useCallback(
    (routePath) =>
      routeOptions.find((opt) => opt.path === routePath)?.slug || "",
    [routeOptions],
  );

  // Clean up Quill when leaving edit mode or when switching to a different document
  React.useEffect(() => {
    if (!isEditing && quillRef.current) {
      quillRef.current = null;
    }
  }, [isEditing]);

  const editingKey = isEditing ? `edit-${selectedDoc?.id ?? "new"}` : "none";

  React.useEffect(() => {
    if (!isEditing || !editorRef.current) return;

    if (!quillRef.current) {
      // Preload container with existing HTML so Quill shows it immediately
      const initialHtml = formData.contentHtml || "";
      if (initialHtml) {
        editorRef.current.innerHTML = initialHtml;
      }
      const editor = new Quill(editorRef.current, {
        theme: "snow",
        modules: { toolbar: quillToolbar },
      });
      editor.on("text-change", () => {
        setFormData((prev) => ({
          ...prev,
          contentHtml: editor.root.innerHTML,
        }));
      });
      editor.getModule("toolbar").addHandler("image", handleQuillImage);
      quillRef.current = editor;
    } else {
      // Already have an editor; sync content when formData.contentHtml changes (e.g. switched doc)
      const editor = quillRef.current;
      const nextHtml = formData.contentHtml || "";
      if (editor && editor.root.innerHTML !== nextHtml) {
        editor.root.innerHTML = nextHtml;
      }
    }
  }, [isEditing, formData.contentHtml, quillToolbar, handleQuillImage]);

  // When the edited document changes, clear Quill ref so the new mount gets a fresh editor
  React.useEffect(() => {
    return () => {
      quillRef.current = null;
    };
  }, [editingKey]);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-baseline gap-3">
            {(selectedDoc || isEditing) && (
              <button
                type="button"
                className="px-3 py-1.5 text-sm font-medium rounded-md border-2 border-blue-600 text-blue-600 bg-white hover:bg-blue-50"
                onClick={() => {
                  setSelectedDoc(null);
                  setIsEditing(false);
                }}
                aria-label="Back to Help list"
              >
                ← Back to Help list
              </button>
            )}
          </div>
          <div className="mt-1 inline-flex items-center gap-2">
            <p className="text-sm text-slate-600">
              Find guidance for each page in the app.
            </p>
            <InlineFieldTooltip
              page="help"
              section="page_description"
              fieldKey="help_overview"
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="p-3 rounded bg-rose-50 text-rose-700 text-sm">
          {error}
        </div>
      )}

      <div className="grid gap-4">
        <div>
          {!selectedDoc && !isEditing && (
            <div className="space-y-4">
              <div className="sticky top-0 z-30 bg-white p-4 rounded-lg shadow-sm border border-slate-200">
                <div className="flex flex-wrap items-center gap-4">
                  <input
                    className="h-10 min-w-[240px] flex-1 border border-slate-200 rounded-md px-3 py-2 text-sm"
                    placeholder="Search documents"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <select
                    className="h-10 min-w-[220px] border border-slate-200 rounded-md px-3 py-2 text-sm"
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                  >
                    <option value="">All Categories</option>
                    {categoryOptions.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                  <select
                    className="h-10 min-w-[220px] border border-slate-200 rounded-md px-3 py-2 text-sm"
                    value={routeFilter}
                    onChange={(e) => setRouteFilter(e.target.value)}
                  >
                    <option value="">All Routes</option>
                    {routeOptions.map((opt) => (
                      <option key={opt.path} value={opt.path}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  {isAdmin && (
                    <button
                      type="button"
                      className="h-10 px-4 bg-primary-600 text-white rounded-md hover:bg-primary-700 shrink-0"
                      onClick={startCreate}
                    >
                      Add Document
                    </button>
                  )}
                </div>
              </div>

              {loading && (
                <div className="text-sm text-slate-500">
                  Loading documents...
                </div>
              )}

              {!loading && Object.keys(groupedDocuments).length === 0 && (
                <div className="text-sm text-slate-500">
                  No help documents yet.
                </div>
              )}

              {!loading && sortedCategories.length > 0 && (
                <div className="grid gap-4 md:grid-cols-12 md:items-start">
                  <div className="md:col-span-4 lg:col-span-3 md:sticky md:top-20 self-start">
                    <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
                      <div className="px-4 py-3 border-b border-slate-200">
                        <h3 className="font-semibold text-slate-800">
                          Categories
                        </h3>
                      </div>
                      <div className="divide-y divide-slate-200 max-h-[calc(100vh-14rem)] overflow-y-auto">
                        {sortedCategories.map((category) => {
                          const count = groupedDocuments[category]?.length || 0;
                          const isActive = activeCategory === category;

                          return (
                            <button
                              key={category}
                              type="button"
                              onClick={() => setActiveCategory(category)}
                              className={`w-full px-4 py-2.5 text-left transition ${
                                isActive
                                  ? "bg-primary-50 text-primary-700"
                                  : "bg-white text-slate-700 hover:bg-slate-50"
                              }`}
                            >
                              <div className="font-medium text-sm">
                                {category} ({count})
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  <div className="md:col-span-8 lg:col-span-9 md:sticky md:top-20 self-start">
                    <div className="bg-white border border-slate-200 rounded-lg shadow-sm flex flex-col max-h-[calc(100vh-14rem)] overflow-hidden">
                      <div className="px-4 py-3 border-b border-slate-200 shrink-0">
                        <h3 className="font-semibold text-slate-800">
                          {activeCategory || "Select a category"}
                        </h3>
                        <p className="text-xs text-slate-500 mt-1">
                          {activeCategory
                            ? "Click a document to open it."
                            : "Choose a category from the left to see its documents."}
                        </p>
                      </div>
                      <div className="overflow-y-auto divide-y divide-slate-200">
                        {activeCategoryDocs.map((doc) => (
                          <div key={doc.id} className="px-4 py-[0.3rem]">
                            <div className="flex items-center justify-between gap-4">
                              <div>
                                <button
                                  type="button"
                                  className="text-left text-primary-700 font-semibold hover:underline"
                                  onClick={() => {
                                    setSelectedDoc(doc);
                                    setIsEditing(false);
                                  }}
                                >
                                  {doc.title}
                                </button>
                              </div>
                              {isAdmin && (
                                <div className="flex gap-2">
                                  <button
                                    type="button"
                                    className="text-blue-600 hover:text-blue-900"
                                    onClick={() => startEdit(doc)}
                                    title="Edit document"
                                  >
                                    <FontAwesomeIcon
                                      icon={faPencil}
                                      className="h-4 w-4"
                                    />
                                  </button>
                                  <button
                                    type="button"
                                    className="text-red-600 hover:text-red-900"
                                    onClick={() => handleDelete(doc)}
                                    title="Delete document"
                                  >
                                    <FontAwesomeIcon
                                      icon={faTrash}
                                      className="h-4 w-4"
                                    />
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {(selectedDoc || isEditing) && (
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-4 space-y-4">
              <div>
                <h2 className="text-xl font-semibold text-slate-900">
                  {selectedDoc?.title || "New Document"}
                </h2>
                {selectedDoc?.routePath && (
                  <p className="text-xs text-slate-500 mt-1">
                    {selectedDoc.routePath}
                  </p>
                )}
              </div>

              {!isEditing && selectedDoc && (
                <div
                  className="help-content"
                  dangerouslySetInnerHTML={{ __html: selectedDoc.contentHtml }}
                />
              )}

              {isEditing && (
                <div className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <label className="text-xs font-semibold text-slate-500">
                        Title
                      </label>
                      <input
                        className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                        value={formData.title}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            title: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-3">
                    <div>
                      <label className="text-xs font-semibold text-slate-500">
                        Route Path
                      </label>
                      <select
                        className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                        value={formData.routePath}
                        onChange={(e) =>
                          setFormData((prev) => {
                            const nextRoutePath = e.target.value;
                            return {
                              ...prev,
                              routePath: nextRoutePath,
                            };
                          })
                        }
                      >
                        <option value="">Select route...</option>
                        {routeOptions.map((opt) => (
                          <option key={opt.path} value={opt.path}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-500">
                        Category
                      </label>
                      <select
                        className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                        value={formData.category}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            category: e.target.value,
                          }))
                        }
                      >
                        {categoryOptions.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-500">
                        Sort Order
                      </label>
                      <input
                        type="number"
                        className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                        value={formData.sortOrder}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            sortOrder: Number(e.target.value) || 0,
                          }))
                        }
                      />
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-4 text-sm">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.isPublished}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            isPublished: e.target.checked,
                          }))
                        }
                      />
                      Published
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.isAdminOnly}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            isAdminOnly: e.target.checked,
                          }))
                        }
                      />
                      Admin only
                    </label>
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-slate-500">
                      Content
                    </label>
                    <div
                      key={editingKey}
                      ref={editorRef}
                      className="bg-white"
                      style={{ minHeight: "240px" }}
                    />
                  </div>

                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
                      onClick={handleSave}
                      disabled={saving}
                    >
                      {saving ? "Saving..." : "Save"}
                    </button>
                    <button
                      type="button"
                      className="px-4 py-2 border border-slate-300 rounded-md text-slate-600"
                      onClick={cancelEdit}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {!isEditing && selectedDoc && isAdmin && (
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="px-4 py-2 border border-slate-300 rounded-md text-slate-700"
                    onClick={() => startEdit(selectedDoc)}
                  >
                    Edit
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Help;
